/**
 * GDPR / T&C consent for PF v2 running inside the CRM embed.
 *
 * The decision itself is shared with the legacy embed
 * (`utils/embedConsent/consentRules`) so the two cannot disagree about when an
 * order may be placed without a recorded acceptance; what lives here is the
 * wiring PF v2 owns — its own `isEmbed`, its own store, its own Place Order gate.
 *
 * Inert outside the embed and for non-clients: the consent endpoints are
 * client-only, and the embed's admin path is a test path. Nothing is fetched and
 * nothing is required, so ordinary orders are untouched.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import constate from 'constate'

import { consentTypesToRecord, isConsentRequired as isConsentRequiredRule, missingConsentTypes } from 'utils/embedConsent/consentRules'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'
import { useGetConsentStatus, useRecordConsent } from 'dataQueries'
import { useUserData } from 'components/contexts/UserDataContext'

import { PfRevampEventName } from '../analytics/events'
import { dispatchPfRevampEvent } from '../analytics/usePfRevampAnalytics'
import { usePfEmbed } from './usePfEmbed'

export interface PfEmbedConsent {
  /** The display decision can be made — the status settled, or none was needed. */
  isConsentResolved: boolean
  /** An acceptance has to be recorded before this order can be placed. */
  isConsentRequired: boolean
  /** Place Order must stay disabled: consent is due and the box is unticked. */
  isConsentBlockingOrder: boolean
  isConsentChecked: boolean
  setIsConsentChecked: (checked: boolean) => void
  /** The value-proposition overlay is due — once per embed session, consent missing. */
  showValueProposition: boolean
  dismissValueProposition: () => void
  /** Records what is missing. Resolves false when the order must be blocked. */
  ensureConsentRecorded: () => Promise<boolean>
  isConsentRecording: boolean
}

/**
 * The hook behind the provider, exported for its own tests. Consumers use
 * {@link usePfEmbedConsentState} — the checkbox and the Place Order gate have to
 * share one `isConsentChecked`, and two calls to this hook would not.
 */
export const usePfEmbedConsent = (): PfEmbedConsent => {
  const { isEmbed } = usePfEmbed()
  const { roles } = useAuth0()
  const { userPropertyWorkspaceId } = useUserData()

  // The workspace gates the GET, so without it the query never settles and
  // `isConsentResolved` would hold the flow on its spinner forever.
  const isApplicable = isEmbed && roles.isClient && !!userPropertyWorkspaceId

  const statusQuery = useGetConsentStatus(userPropertyWorkspaceId ?? '', 'PROPSTACK', isApplicable)
  const recordConsent = useRecordConsent()

  const missing = useMemo(() => missingConsentTypes(statusQuery.data), [statusQuery.data])

  const isConsentResolved = !isApplicable || statusQuery.isSuccess || statusQuery.isError
  const isConsentRequired = isConsentRequiredRule({
    isApplicable,
    hasError: statusQuery.isError,
    missing,
  })

  useEffect(() => {
    if (!statusQuery.isError) return
    logger.warn('Embed consent status fetch failed — failing closed (consent re-asked)', undefined, {
      context: { component: 'usePfEmbedConsent', action: 'consentStatusFetchFailed' },
    })
  }, [statusQuery.isError])

  const [isConsentChecked, setIsConsentChecked] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const dismissValueProposition = useCallback(() => setIsDismissed(true), [])

  const showValueProposition = isConsentRequired && !isDismissed

  // Funnel denominator: once per embed session, so the per-card click-through is measurable.
  const shownLogged = useRef(false)
  useEffect(() => {
    if (!showValueProposition || shownLogged.current) return
    shownLogged.current = true
    dispatchPfRevampEvent({ type: PfRevampEventName.EMBED_OVERLAY_SHOWN, payload: {} })
  }, [showValueProposition])

  const isConsentBlockingOrder = isConsentRequired && !isConsentChecked

  const ensureConsentRecorded = useCallback(async (): Promise<boolean> => {
    if (!isConsentRequired) return true

    const consentTypes = consentTypesToRecord(missing)
    try {
      await recordConsent.mutateAsync({
        workspaceId: userPropertyWorkspaceId,
        channel: 'PROPSTACK',
        consentTypes,
      })
      dispatchPfRevampEvent({ type: PfRevampEventName.EMBED_CONSENT_RECORDED, payload: { consentTypes } })
      return true
    } catch (error) {
      // 409 is benign: a concurrent request already persisted the acceptance, and
      // blocking here would strand a client who has just accepted.
      if (error instanceof AxiosError && error.response?.status === 409) {
        statusQuery.refetch()
        return true
      }
      logger.warn('Recording embed consent failed — order placement blocked', error, {
        context: { component: 'usePfEmbedConsent', action: 'recordConsentFailed' },
      })
      return false
    }
  }, [isConsentRequired, missing, recordConsent, userPropertyWorkspaceId, statusQuery])

  return {
    isConsentResolved,
    isConsentRequired,
    isConsentBlockingOrder,
    isConsentChecked,
    setIsConsentChecked,
    showValueProposition,
    dismissValueProposition,
    ensureConsentRecorded,
    isConsentRecording: recordConsent.isPending,
  }
}

/**
 * One consent state for the whole flow. Mounted at the PF v2 root; the overlay,
 * the Summary checkbox and the Place Order gate all read the same instance.
 */
const [PfEmbedConsentProvider, usePfEmbedConsentContext] = constate(usePfEmbedConsent)

export { PfEmbedConsentProvider }

/**
 * What a consumer sees with no provider above it: no embed, so nothing is due
 * and nothing blocks.
 *
 * constate hands back an empty object outside its provider with only a
 * `console.warn`, which would make `ensureConsentRecorded` undefined and throw
 * at the moment an order is placed. A defined inert value is the safe direction
 * here precisely because consent cannot apply outside the embed, and the embed
 * always renders under the provider at the PF v2 root.
 */
const INERT: PfEmbedConsent = {
  isConsentResolved: true,
  isConsentRequired: false,
  isConsentBlockingOrder: false,
  isConsentChecked: false,
  setIsConsentChecked: () => undefined,
  showValueProposition: false,
  dismissValueProposition: () => undefined,
  ensureConsentRecorded: () => Promise.resolve(true),
  isConsentRecording: false,
}

/** Consent state for the current flow, inert when no provider is mounted. */
export const usePfEmbedConsentState = (): PfEmbedConsent => ({ ...INERT, ...usePfEmbedConsentContext() })
